import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges, OnInit, DoCheck, OnDestroy } from '@angular/core';
import { FormGroup, ReactiveFormsModule, FormControl, Validators } from '@angular/forms';
import { MatDropdownFieldComponent } from "../mat-dropdown-field/mat-dropdown-field.component";
import { ButtonFieldComponent } from "../button-field/button-field.component";
import { MatTextFieldComponent } from '../mat-text-field/mat-text-field.component';
import { MatDateFieldComponent } from '../mat-date-field/mat-date-field.component';
import { FIELD_TYPES, Props, DEFAULT_PROPS, DynamicFormArray, FieldSelectedValue, FormFieldConfig, LayoutSearchConfig } from '../../../model/form.model';
import { ButtonNames, BUTTON_NAMES } from '../../../model/common.model';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { FormService } from '../../../services/form.service';
import { FieldNames, FieldTypesForOptions } from '../../../model/form-fields.model';
import { DistributionListFilterContainerComponent } from "../../../components/distribution-list-filter-container/distribution-list-filter-container.component";
import { MatCheckboxListFieldComponent } from '../mat-checkbox-list-field/mat-checkbox-list-field.component';
import { MatTextareaComponent } from '../mat-textarea/mat-textarea.component';
import { MatFileUploadComponent } from '../mat-file-upload/mat-file-upload.component';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { DataTableSchema } from '../../../model/dynamic-data-table.model';
import { AdjustedTenureFilterForm } from '../../../model/tenure/tenure-adjustment.model';
@Component({
  selector: 'app-form-container',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatTextareaComponent, MatFileUploadComponent, MatDropdownFieldComponent, MatTextFieldComponent, MatDateFieldComponent, MatCheckboxListFieldComponent, ButtonFieldComponent, DistributionListFilterContainerComponent],
  templateUrl: './form-container.component.html',
  styleUrl: './form-container.component.scss',
  providers: [
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline', subscriptSizing: 'dynamic' }
    }
  ]
})
export class FormContainerComponent implements OnChanges, OnInit, DoCheck, OnDestroy {
  readonly FieldType: typeof FIELD_TYPES = FIELD_TYPES;
  public form!: any;
  public dynamicFieldWidth = 95;
  public searchCritiria: any = {};
  @Input() props: Props = DEFAULT_PROPS;
  @Input() DataTableSchema?: DataTableSchema;
  @Input() actionRequired = false;
  @Input() elementId = '';
  @Input() dynamicFormArray!: DynamicFormArray;
  @Input() formForFilter = true;
  @Output() jobRoleContainerClicked = new EventEmitter<any>();
  showJobRoleContainer = false;
  isJobReset = false;
  chekcBoxFieldsArray: FormFieldConfig[] = [];
  private readonly originalFieldDisabledState = new Map<string, boolean>();
  private previousLoadingState = false;
  private readonly destroy$ = new Subject<void>();
  constructor(private readonly router: Router, private readonly formService: FormService) { }

  onValueChanged(event: FieldSelectedValue): void {
    this.searchCritiria[event['FieldName']] = event['Value']
    this.changeCallback(this.form['controls'][event['FieldName']])
    if (this.dynamicFormArray.onControlValueChanged) {
      this.dynamicFormArray['onControlValueChanged']({ FieldName: event.FieldName, Value: event.Value, FieldArray: this.dynamicFormArray['FieldsArray'] })
    }
    if (this.props.showJobRoleContainer && event['FieldName'] === FieldNames.ElevateNewsletter) {
      this.toggleChildJobRoleContainer(event['FieldName'], event.Value)
    }
  }
  onButtonClicked(name: ButtonNames) {
    if (name === BUTTON_NAMES.Create && this.form.invalid) {
      this.form.markAllAsTouched(); // triggers validation errors
      return;
    }
    if (name === BUTTON_NAMES.Reset && this.props.showJobRoleContainer) {
      this.isJobReset = !this.isJobReset;
      this.showJobRoleContainer = false;
    }
    if (this.dynamicFormArray['searchConfig']) {
      const postParams: LayoutSearchConfig = {
        searchConfig: {
          ...structuredClone(this.dynamicFormArray['searchConfig']),
        }
      }
      if (typeof this.dynamicFormArray?.onButtonClicked === 'function') {
        this.dynamicFormArray.onButtonClicked({ name, postParams, source: 'user' });
      }
    } else {
      this.dynamicFormArray['onButtonClicked']({ name, source: 'user' });
    }
    // Reset form controls after reset action
    if (name === BUTTON_NAMES.Reset || name === BUTTON_NAMES.Cancel) {
      setTimeout(() => {
        this.dynamicFormArray.FieldsArray.forEach(field => {
          const value = field.selectedValue ?? field.defaultValue ?? '';
          const control = this.form.get(field.fieldName);
          this.resetControl(control, value);
          if (field['isAutoComplete']) {
            this.resetControl(this.form.get(`${field.fieldName}_autoComplete`), value);
            this.searchCritiria[field.fieldName] = value;
          }
        });
        this.changeCallback(null);
        if (name === BUTTON_NAMES.Reset && this.dynamicFormArray.autoSearchOnLoad) {
          const hasDefaultValues = this.dynamicFormArray.FieldsArray
            .filter(f => !f.hidden)
            .some(f => this.isValuePresent(this.form?.get(f.fieldName)?.value));
          if (hasDefaultValues) {
            this.onButtonClicked(BUTTON_NAMES.Search);
          }
        }
      });
    }
    if (this.props.showJobRoleContainer && name === BUTTON_NAMES.Search && this.showJobRoleContainer) {
      this.toggleChildJobRoleContainer();
    }
  }
  private resetControl(control: FormControl | null, value: any): void {
    if (!control) {return};
    control.setValue(value, { emitEvent: false });
    control.markAsPristine();
    control.markAsUntouched();
  }
  ngOnInit() {
    this.form = this.makeFormField(this.dynamicFormArray.FieldsArray);
    this.form['formName'] = this.dynamicFormArray['formName']
    this.props = this.props ?? {};
    //Reset the component when routes changes
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (typeof this.dynamicFormArray?.onButtonClicked === 'function') {
          this.dynamicFormArray?.onButtonClicked({ name: BUTTON_NAMES.Reset, source: 'navigation' });
        }
      });
    this.formService.sendForm(this.form, this.dynamicFormArray.FieldsArray, this.dynamicFormArray?.formName);
    this.previousLoadingState = !!this.DataTableSchema?.isLoading;
    this.applyTableLoadingState(this.previousLoadingState);
    setTimeout(() => {
      this.changeCallback(null);
      if (this.dynamicFormArray.autoSearchOnLoad) {
        const hasDefaultValues = this.dynamicFormArray.FieldsArray
          .filter(f => !f.hidden)
          .some(f => this.isValuePresent(this.form?.get(f.fieldName)?.value));
        if (hasDefaultValues) {
          this.onButtonClicked(BUTTON_NAMES.Search);
        }
      }
    });
  }

  ngDoCheck(): void {
    const currentLoadingState = !!this.DataTableSchema?.isLoading;
    if (currentLoadingState === this.previousLoadingState) {
      return;
    }
    this.previousLoadingState = currentLoadingState;
    this.applyTableLoadingState(currentLoadingState);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['dynamicFormArray']?.currentValue) {
      this.form = this.makeFormField(this.dynamicFormArray.FieldsArray);
      this.formService.sendForm(this.form, this.dynamicFormArray.FieldsArray, this.dynamicFormArray?.formName);
      this.previousLoadingState = !!this.DataTableSchema?.isLoading;
      this.applyTableLoadingState(this.previousLoadingState);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applyTableLoadingState(isLoading: boolean): void {
    if (!this.dynamicFormArray?.FieldsArray || !this.form) {
      return;
    }
    if (this.dynamicFormArray.disableFieldsDuringLoad === false) {
      return;
    }
    const isTenureAdjustmentFilter = this.dynamicFormArray?.formName === AdjustedTenureFilterForm;
    this.dynamicFormArray.FieldsArray.forEach(field => {
      if (!this.originalFieldDisabledState.has(field.fieldName)) {
        this.originalFieldDisabledState.set(field.fieldName, !!field.disabled);
      }

      const control = this.form.get(field.fieldName);
      const autoCompleteControl = field['isAutoComplete'] ? this.form.get(`${field.fieldName}_autoComplete`) : null;
      if (isLoading) {
        if (isTenureAdjustmentFilter) {
          this.originalFieldDisabledState.set(field.fieldName, !!field.disabled);
        }
        field.disabled = true;
        control?.disable({ emitEvent: false });
        autoCompleteControl?.disable({ emitEvent: false });
        return;
      }

      const shouldDisable = this.originalFieldDisabledState.get(field.fieldName) ?? (isTenureAdjustmentFilter ? !!field.disabled : false);
      field.disabled = shouldDisable;
      if (shouldDisable) {
        control?.disable({ emitEvent: false });
        autoCompleteControl?.disable({ emitEvent: false });
      } else {
        control?.enable({ emitEvent: false });
        autoCompleteControl?.enable({ emitEvent: false });
      }
    });
  }


  makeFormField(fieldInfo: Array<FormFieldConfig>): FormGroup {
    const formControls: any = {}
    fieldInfo?.forEach(field => {
      let fieldValue: any = field['selectedValue'] || '';
      this.searchCritiria[field['fieldName']] = fieldValue;
      if (field['disabled']) {
        fieldValue = { value: fieldValue, disabled: true }
      }
      const validators = [];
      if (field.required) {
        validators.push(Validators.required)
      }
      if (field.fieldType === FIELD_TYPES.EMAIL) {
        validators.push(Validators.email)
      }
      if (field['isAutoComplete']) {
        const controlName = field['fieldName'] + '_autoComplete';
        formControls[controlName] = new FormControl(fieldValue, validators)
      }
      formControls[field['fieldName']] = new FormControl(fieldValue, validators)
      formControls[field['fieldName']].valueChanges.subscribe((_value: any) => {
        setTimeout(() => {
          this.changeCallback(formControls[field['fieldName']])
        })
      });
    })
    return new FormGroup(formControls)
  }
  private isValuePresent(val: any): boolean {
    if (val === undefined || val === null || val === ''){ return false };
    if (val instanceof Date){ return !Number.isNaN(val.getTime())};
    if (typeof val === 'object' && val !== null && ('year' in val && 'month' in val && 'day' in val)){ return true} ;
    if (typeof val === 'object' && (typeof (val as any).toDate === 'function' || (val as any)._d)){ return true};
    if (typeof val === 'string'){ return val.trim() !== ''};
    if (typeof val === 'number'){ return val !== 0};
    if (Array.isArray(val)){ return val.length > 0};
    if (typeof val === 'object' && !Array.isArray(val)){ return Object.keys(val).length > 0};
    return false;
  }
  changeCallback(_formcontrol: any) {
    if (!this.dynamicFormArray?.['FieldsArray']) { return };
    const filterValueExist = this.dynamicFormArray['FieldsArray']
      .filter(f => !f.hidden)
      .some(f => this.isValuePresent(this.form?.get(f.fieldName)?.value));
    this.enableOrDisableButtons(filterValueExist);
  }
  enableOrDisableButtons(enableButton: boolean) {
    this.dynamicFormArray['ButtonsArray']?.forEach(button => {
      if (button['customEnableDisable']) { return };
      if (button.name === BUTTON_NAMES.Search || button.name === BUTTON_NAMES.Reset) {
        button['isDisabled'] = !enableButton;
        button['detectChanges']?.();
      }
    });
  }

  useActionButtonsForTextSearch(): boolean {
    return !!this.dynamicFormArray?.FieldsArray?.some(
      (field) => field.fieldType === FIELD_TYPES.TEXTSEARCH || !!field.fieldActionConfig
    );
  }

  shouldShowFooterButton(buttonName: ButtonNames): boolean {
    if (!this.useActionButtonsForTextSearch()) {
      return true;
    }
    return buttonName !== BUTTON_NAMES.Search && buttonName !== BUTTON_NAMES.Reset;
  }

  hasVisibleFooterButtons(): boolean {
    const buttons = this.dynamicFormArray?.ButtonsArray ?? [];
    return buttons.some((button) => this.shouldShowFooterButton(button.name));
  }

  toggleChildJobRoleContainer(field?: any, value?: string) {
    this.showJobRoleContainer = (field) ? true : !this.showJobRoleContainer;
    if (field && value !== "") {
      this.dynamicFormArray.FieldsArray.forEach(filterField => {
        if (FieldTypesForOptions.includes(filterField['fieldType']) && (filterField['preSelectedValues'])) {
          filterField.selectedValue = (value === "Y") ? filterField['preSelectedValues'] : ""
        }
      })
    }
  }

  openJobRoleSelection(event?: Event) {
    event?.stopPropagation();
    this.showJobRoleContainer = true;
  }

  closeJobRoleSelection() {
    this.showJobRoleContainer = false;
  }
}

